import React, { createContext, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';

// Creates a React context for managing the set of selected icon names.
const IconSelectionContext = createContext();

// A React component that provides the icon selection state (selectedIconNames)
// and an updater function (setSelectedIconNames) to its children via context.
// It initializes the state from localStorage and persists changes back to localStorage.
export const IconProvider = ({ children }) => {
    const [selectedIconNames, setSelectedIconNames] = useState(() => {
        // Initializes the selectedIconNames state from localStorage or defaults to an empty Set.
        try {
            const storedValue = localStorage.getItem('selectedIconNames');
            return storedValue ? new Set(JSON.parse(storedValue)) : new Set();
        } catch (error) {
            return new Set();
        }
    });

    useEffect(() => {
        // Persists the selectedIconNames state to localStorage whenever it changes.
        try {
            localStorage.setItem('selectedIconNames', JSON.stringify(Array.from(selectedIconNames)));
        } catch (error) {
            // Intentionally ignore localStorage saving errors in this version.
        }
    }, [selectedIconNames]);

    const contextValue = useMemo(() => {
        // Memoizes the context value to prevent unnecessary re-renders.
        return {
            selectedIconNames,
            setSelectedIconNames,
        };
    }, [selectedIconNames]);

    return (
        <IconSelectionContext.Provider value={contextValue}>
            {children}
        </IconSelectionContext.Provider>
    );
};

IconProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

// A custom React hook that provides easy access to the IconSelectionContext.
// It returns the selected icon names and the function to update them.
// This hook must be used within a component that is a descendant of IconProvider.
export const useIconSelection = () => {
    const context = React.useContext(IconSelectionContext);
    if (context === undefined) {
        throw new Error('useIconSelection must be used within an IconProvider');
    }
    return context;
};